import { useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import StepHeader from '@components/step-header/StepHeader';
import DateRange from '@components/date-range/DateRange';
import TextAreaInput from '@components/textarea/TextArea';
import Checkbox from '@components/checkbox/Checkbox';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { dateBeforeB } from '@src/utils/helpers';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { useRequest } from '@stores';
import { useResetDirtyFields } from '@hooks';

export const ViolationsFormGeneralSiteInformationMetadata = {
  sectionName: 'General Site Information',
};

const schema = yup.object().shape({
  descriptionOfAllegedViolation: yup
    .string()
    .test(
      'isDefault',
      'DEFAULT_VALUE is a reserved word, please enter a valid string',
      (val) => val.toUpperCase() !== 'DEFAULT_VALUE'
    )
    .required('Field is required'),
  waterBodyType: yup.string().required('Field is required'),
  materialDischarged: yup.string().required('Field is required'),
  equipmentUsed: yup.string().required('Field is required'),
  isWorkOngoing: yup.boolean().required('Check a value'),
});

const ViolationsFormGeneralSiteInformation = connect(
  'doUpdateViolationComplaintRequest',
  'selectViolationComplaintData',
  'selectRequestAPIData',
  'selectIsAnonymous',
  ({ doUpdateViolationComplaintRequest, violationComplaintData, requestAPIData, isAnonymous, stepNo }) => {
    const defaultValues = {
      descriptionOfAllegedViolation: violationComplaintData?.descriptionOfAllegedViolation ?? '',
      waterBodyType: violationComplaintData?.waterBodyType ?? '',
      materialDischarged: violationComplaintData?.materialDischarged ?? '',
      equipmentUsed: violationComplaintData?.equipmentUsed ?? '',
      workBeganDate: violationComplaintData?.workBeganDate
        ? violationComplaintData.workBeganDate.slice(0, 10)
        : ('' ?? ''),
      workEndDate: violationComplaintData?.workEndDate ? violationComplaintData.workEndDate.slice(0, 10) : ('' ?? ''),
      isWorkOngoing: violationComplaintData?.isWorkOngoing ?? false,
    };

    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();
    const methods = useForm({ resolver: yupResolver(schema), mode: 'onBlur', defaultValues: defaultValues });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      getValues,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const isValid = Object.keys(errors).length === 0;
    const isTouched = isSectionTouched(stepNo);

    const isWorkOngoing = watch('isWorkOngoing');

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    //Reset the dirty states of the fields after a save.
    !isAnonymous && useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      updateSection(stepNo, {
        valid: isReadOnly ? true : isValid,
        label: ViolationsFormGeneralSiteInformationMetadata.sectionName,
      });
    }, [isValid, updateSection, stepNo, isReadOnly]);

    useEffect(() => {
      isWorkOngoing && setValue('workEndDate', null);
    }, [isWorkOngoing, setValue]);

    const handleChange = () => {
      const values = getValues();
      const newValues = {
        ...values,
        descriptionOfAllegedViolation: values?.descriptionOfAllegedViolation,
      };
      doUpdateViolationComplaintRequest(newValues);
    };

    useEffect(() => {
      trigger();
    }, [trigger]);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}

        <StepHeader
          text='General Site Information'
          subtext='Provide a detailed description of the alleged violation/activity (type of waterbody affected, estimated amount of impact, type of
              fill material, and type of equipment being used). Include start and end dates of observed activity, if known. Be as specific as possible.
              Provide a general description if any of this information is unknown.'
        />
        <div>
          <TextAreaInput
            name='descriptionOfAllegedViolation'
            label='General Description of Alleged Violation (provide as much information as possible, including the purpose of the work and estimated amount of impact, if known)'
            onChange={handleChange}
            required
            readOnly={isReadOnly}
          />
        </div>
        <div>
          <TextAreaInput
            name='waterBodyType'
            label='Type of Waterbody Affected (e.g., river, stream, lake, pond, wetland, etc.)'
            onChange={handleChange}
            required
            readOnly={isReadOnly}
          />
        </div>
        <div>
          <TextAreaInput
            name='materialDischarged'
            label='Type of Material Discharged (e.g., Dirt, Gravel, Rock, Construction Debris, Tires, Other, etc.)'
            onChange={handleChange}
            required
            readOnly={isReadOnly}
          />
        </div>
        <div>
          <TextAreaInput
            name='equipmentUsed'
            label='Equipment Used (e.g., backhoe, bulldozer, dump truck, etc.)'
            onChange={handleChange}
            required
            readOnly={isReadOnly}
          />
        </div>
        <div className='row margin-bottom-3 margin-top-2'>
          <div className='col-lg-8'>
            <DateRange
              nameBefore='workBeganDate'
              nameAfter='workEndDate'
              labelBefore='Date Work Began'
              labelAfter='Date Work Ended'
              dateBefore={dateBeforeB}
              onChange={handleChange}
              isOnGoing={isWorkOngoing}
              readOnly={isReadOnly}
            />
          </div>
          <div className='col-lg-4 padding-top-3'>
            <fieldset id='isWorkOngoing'>
              <Checkbox
                id='work-ongoing'
                name='isWorkOngoing'
                label='Check box if the work is still ongoing'
                onChange={handleChange}
                disabled={isReadOnly}
              />
            </fieldset>
          </div>
        </div>
      </FormProvider>
    );
  }
);
ViolationsFormGeneralSiteInformation.metadata = ViolationsFormGeneralSiteInformationMetadata;

export default ViolationsFormGeneralSiteInformation;
